import React from 'react';
import { motion } from 'framer-motion';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Calendar,
  Plus,
  BarChart3,
  Settings,
  Users,
  LogOut,
  ChevronLeft,
  Shield,
  MapPin,
  FileText,
  CalendarRange,
} from 'lucide-react';
import { useAuthActions } from '@/store/authStore';
import { usePermissions } from '@/hooks/usePermissions';
import { Permission } from '@/types/permissions';
import { clsx } from 'clsx';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

type NavigationItem = {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
  exact?: boolean;
  permission?: Permission;
  permissions?: Permission[];
};

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const { logout } = useAuthActions();
  const { t } = useTranslation();
  const { hasPermission, user } = usePermissions();

  // 基础导航（所有用户可见）
  const baseNavigation: NavigationItem[] = [
    {
      name: t('nav.dashboard'),
      href: '/',
      icon: LayoutDashboard,
      exact: true,
    },
    {
      name: t('nav.bookings'),
      href: '/bookings',
      icon: Calendar,
      permission: Permission.VIEW_BOOKINGS,
    },
    {
      name: t('nav.createBooking'),
      href: '/bookings/create',
      icon: Plus,
      permission: Permission.CREATE_BOOKING,
    },
    {
      name: t('nav.programme'),
      href: '/programme',
      icon: CalendarRange,
      permissions: [Permission.MANAGE_PROGRAMMES, Permission.MANAGE_PRIVATE_LESSONS, Permission.MANAGE_CONTENT],
    },
    {
      name: t('common.customers'),
      href: '/customers',
      icon: Users,
      permission: Permission.VIEW_CUSTOMERS,
    },
  ];

  // 高级功能导航（需要特定权限）
  const advancedNavigation: NavigationItem[] = [
    {
      name: t('nav.analytics'),
      href: '/analytics',
      icon: BarChart3,
      permission: Permission.VIEW_ANALYTICS,
    },
    {
      name: t('nav.contentManagement'),
      href: '/content',
      icon: FileText,
      permission: Permission.MANAGE_CONTENT,
    },
    {
      name: t('courts.title'),
      href: '/courts',
      icon: MapPin,
      permission: Permission.MANAGE_COURTS,
    },
    {
      name: t('settings.staffManagement'),
      href: '/staff',
      icon: Shield,
      permission: Permission.MANAGE_STAFF,
    },
    {
      name: t('nav.settings'),
      href: '/settings',
      icon: Settings,
      permission: Permission.SYSTEM_SETTINGS,
    },
  ];


  // 过滤导航项：只显示用户有权限的项目
  const filterNavigationByPermission = (navItems: NavigationItem[]) =>
    navItems.filter(item => {
      // 如果没有权限要求，显示给所有用户
      if (!item.permission && (!item.permissions || item.permissions.length === 0)) return true;

      if (item.permission && hasPermission(item.permission)) return true;
      if (Array.isArray(item.permissions) && item.permissions.some((permission) => hasPermission(permission))) {
        return true;
      }
      return false;
    });

  const navigation = [
    ...baseNavigation,
    ...filterNavigationByPermission(advancedNavigation),
  ].filter(item => {
    if (!item.permission && (!item.permissions || item.permissions.length === 0)) return true;
    if (item.permission && hasPermission(item.permission)) return true;
    if (Array.isArray(item.permissions) && item.permissions.some((permission) => hasPermission(permission))) {
      return true;
    }
    return false;
  });

  return (
    <motion.div
      initial={false}
      animate={{ width: isCollapsed ? 80 : 280 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="fixed left-0 top-0 h-screen bg-white border-r border-gray-200 z-30 shadow-soft touch-manipulation"
    >
      <div className="flex flex-col h-full">
        {/* Logo和折叠按钮 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <motion.div
            animate={{ opacity: isCollapsed ? 0 : 1 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-3"
          >
            <div className="w-8 h-8 rounded-xl bg-gradient-primary flex items-center justify-center">
              <span className="text-white font-bold">N</span>
            </div>
            {!isCollapsed && (
              <div>
                <h1 className="text-lg font-bold text-gray-900">Nexcave</h1>
                <p className="text-xs text-gray-500">{t('common.staffPortal')}</p>
              </div>
            )}
          </motion.div>
          
          <button
            onClick={onToggle}
            className={clsx(
              "p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors",
              isCollapsed && "rotate-180"
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>

        {/* 导航菜单 */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const [pathPart, queryPart] = item.href.split('?');
            const pathMatched = item.exact
              ? location.pathname === pathPart
              : location.pathname.startsWith(pathPart);
            const currentSearch = new URLSearchParams(location.search);
            const expectedSearch = new URLSearchParams(queryPart || '');
            const hasQueryVariant = !queryPart && navigation.some(
              nav => nav !== item && nav.href.startsWith(`${pathPart}?`)
            );
            const queryMatched = Array.from(expectedSearch.entries()).every(
              ([key, value]) => currentSearch.get(key) === value
            ) && (!hasQueryVariant || !location.search);
            const isActive = pathMatched && queryMatched;

            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={clsx(
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 relative group touch-manipulation",
                  isActive
                    ? "bg-primary-50 text-primary-700"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                )}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                
                <motion.span
                  animate={{ opacity: isCollapsed ? 0 : 1 }}
                  transition={{ duration: 0.2 }}
                  className="truncate"
                >
                  {item.name}
                </motion.span>

                {/* 徽章 */}
                {item.badge && !isCollapsed && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="ml-auto bg-primary-100 text-primary-700 text-xs font-medium px-2 py-0.5 rounded-full"
                  >
                    {item.badge}
                  </motion.span>
                )}

                {/* 折叠状态下的提示 */}
                {isCollapsed && (
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                    {item.name}
                    {item.badge && (
                      <span className="ml-2 bg-primary-500 text-white px-1.5 py-0.5 rounded-full text-xs">
                        {item.badge}
                      </span>
                    )}
                  </div>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* 用户信息和退出 */}
        <div className="border-t border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">
                {user?.full_name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            
            <motion.div
              animate={{ opacity: isCollapsed ? 0 : 1 }}
              transition={{ duration: 0.2 }}
              className="flex-1 min-w-0"
            >
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.full_name || t('common.unknownUser')}
              </p>
              <p className="text-xs text-gray-500 capitalize">
                {user?.role || 'staff'}
              </p>
            </motion.div>
          </div>

          <button
            onClick={logout}
            className={clsx(
              "flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 transition-all duration-200 group touch-manipulation",
              isCollapsed && "justify-center"
            )}
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            
            <motion.span
              animate={{ opacity: isCollapsed ? 0 : 1 }}
              transition={{ duration: 0.2 }}
            >
              {t('auth.logout')}
            </motion.span>

            {/* 折叠状态下的提示 */}
            {isCollapsed && (
              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                {t('auth.logout')}
              </div>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default Sidebar;
